import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faCalendarAlt,
    faCreditCard,
    faGem,
    faReceipt,
    faTriangleExclamation,
    faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { BRAND_NAME } from "../brand";

import PageContainer from "./PageContainer";
import { useAppBootstrap } from "../context/AppBootstrapContext";
import {
    BillingCycle,
    BillingInvoice,
    EntitlementSnapshot,
    SubscriptionSnapshot,
    subscriptionApi,
} from "../api/subscription";
import {
    cancellationScheduledLine,
    formatAccessUntilLabel,
    isTrialEndingSoon,
    trialActiveCalmLine,
    trialEndedEffectiveFreeLine,
    trialEndingSoonBannerLine,
    trialEndingSoonDaysLeft,
} from "../utils/commercialPlanMessaging";
import {
    billingEffectivePlan,
    billingLimitsFromEntitlements,
    pollUntilProSynced,
    proUpsellPreviewLimits,
} from "../utils/commercialPlanDisplay";

let stripePromise: Promise<any | null> | null = null;
if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    try {
        // Keep tests/build resilient if Stripe SDK is unavailable in the local environment.
        const { loadStripe } = require("@stripe/stripe-js");
        stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    } catch {
        stripePromise = null;
    }
}

const formatCurrency = (amountInCents: number, currency = "usd") => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format((amountInCents || 0) / 100);
    } catch {
        return `$${((amountInCents || 0) / 100).toFixed(2)}`;
    }
};

const formatDate = (value?: string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString();
};

const Billing: React.FC = () => {
    const { bootstrap, refresh } = useAppBootstrap();
    const location = useLocation();
    const navigate = useNavigate();

    const [subscription, setSubscription] = useState<SubscriptionSnapshot | null>(null);
    const [entitlements, setEntitlements] = useState<EntitlementSnapshot | null>(null);
    const [planCycle, setPlanCycle] = useState<BillingCycle>("monthly");
    const [billingHistory, setBillingHistory] = useState<BillingInvoice[]>([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    const bootstrapRef = useRef(bootstrap);
    const entitlementsRef = useRef(entitlements);
    const isMountedRef = useRef(true);
    const checkoutSuccessPollStartedRef = useRef(false);
    const checkoutPollSignalRef = useRef<{ cancelled: boolean } | null>(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (checkoutPollSignalRef.current) {
                checkoutPollSignalRef.current.cancelled = true;
            }
        };
    }, []);

    useEffect(() => {
        bootstrapRef.current = bootstrap;
    }, [bootstrap]);

    useEffect(() => {
        entitlementsRef.current = entitlements;
    }, [entitlements]);

    useEffect(() => {
        if (bootstrap?.subscription) {
            setSubscription(bootstrap.subscription);
        } else if (bootstrap?.user?.subscription) {
            setSubscription(bootstrap.user.subscription);
        }
        setEntitlements(bootstrap?.entitlements || bootstrap?.user?.entitlements || null);
    }, [bootstrap]);

    const loadSubscription = async () => {
        try {
            const statusBundle = await subscriptionApi.getStatusBundle();
            if (isMountedRef.current) {
                setSubscription(statusBundle.subscription);
                setEntitlements(statusBundle.entitlements || null);
            }
            return statusBundle;
        } catch {
            if (isMountedRef.current) {
                setSubscription(null);
                setEntitlements(null);
            }
            return null;
        }
    };

    const loadBillingHistory = async () => {
        setBillingLoading(true);
        try {
            const invoices = await subscriptionApi.getBillingHistory(30);
            setBillingHistory(invoices);
        } catch {
            setBillingHistory([]);
        } finally {
            setBillingLoading(false);
        }
    };

    useEffect(() => {
        const billingState = new URLSearchParams(location.search).get("billing");
        if (billingState !== "success") {
            void loadSubscription();
        }
        void loadBillingHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- initial mount only; skip duplicate fetch when checkout poll owns subscription load
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const billingState = params.get("billing");
        if (!billingState) {
            return;
        }

        navigate("/app/billing", { replace: true });

        if (billingState === "cancel" || billingState === "cancelled") {
            setMessage("Checkout canceled. Your subscription remains unchanged.");
            void refresh();
            return;
        }

        if (billingState !== "success") {
            return;
        }

        if (checkoutSuccessPollStartedRef.current) {
            return;
        }
        checkoutSuccessPollStartedRef.current = true;

        const pollSignal = { cancelled: false };
        checkoutPollSignalRef.current = pollSignal;

        void (async () => {
            const synced = await pollUntilProSynced({
                refresh,
                loadSubscription,
                getBootstrap: () => bootstrapRef.current,
                getEntitlements: () => entitlementsRef.current,
                signal: pollSignal,
            });

            if (!isMountedRef.current || pollSignal.cancelled) {
                return;
            }

            if (synced) {
                setMessage("Payment confirmed. Your Pro plan is active.");
            } else {
                setMessage(
                    "Payment received. Your plan is still updating — refresh this page in a moment or check Billing again shortly.",
                );
            }
        })();
    }, [location.search, navigate, refresh]);

    const effectiveEntitlements = entitlements || bootstrap?.entitlements || bootstrap?.user?.entitlements || null;
    const mirrorSubscriptionTier = (subscription?.tier || "free").toLowerCase();
    const effectivePlan = billingEffectivePlan(bootstrap, effectiveEntitlements);
    const effectiveStatus = (subscription?.status || "active").toLowerCase();
    const trialDaysLeft = effectiveStatus === "trial" ? trialEndingSoonDaysLeft(subscription) : null;
    const trialEndingSoon = Boolean(effectiveStatus === "trial" && isTrialEndingSoon(subscription));
    const trialEndedLine = trialEndedEffectiveFreeLine(bootstrap?.user?.plan, bootstrap?.user?.effective_plan);
    const cancellationScheduled = Boolean(subscription?.cancel_at_period_end && mirrorSubscriptionTier === "pro");
    const cancellationBannerLine = cancellationScheduledLine(formatAccessUntilLabel(subscription?.access_until || subscription?.cancel_at));
    const canResume = Boolean(subscription?.can_resume && cancellationScheduled);

    const annualSavingsPercent = 17;
    const annualEffectiveMonthly = useMemo(() => (9900 / 12 / 100).toFixed(2), []);
    const billingLimits = billingLimitsFromEntitlements(effectiveEntitlements, bootstrap?.mailbox);
    const creditResetDate = formatDate(billingLimits.creditResetDate);
    const includedCredits = billingLimits.includedCredits;
    const usedCredits = billingLimits.usedCredits;
    const remainingCredits = billingLimits.remainingCredits;
    const mailboxLimit = billingLimits.mailboxLimit;
    const connectedMailboxes = billingLimits.connectedMailboxes;
    const mailboxAddonPrice = formatCurrency(billingLimits.mailboxAddonPriceCents, "usd");
    const proUpsellPreview = proUpsellPreviewLimits();
    const proCreditsPreview = proUpsellPreview.credits;
    const proMailboxPreview = proUpsellPreview.mailboxes;
    const limitsUnavailable = !billingLimits.entitlementsLoaded;
    const safeBillingHistory = Array.isArray(billingHistory) ? billingHistory : [];

    const handleUpgrade = async () => {
        setCheckoutLoading(true);
        setMessage(null);
        try {
            const successUrl = `${window.location.origin}/app/billing?billing=success`;
            const cancelUrl = `${window.location.origin}/app/billing?billing=cancel`;
            const result = await subscriptionApi.createCheckout(planCycle, successUrl, cancelUrl);

            if (result.checkout_session_id && stripePromise) {
                const stripe = await stripePromise;
                if (stripe) {
                    const redirect = await stripe.redirectToCheckout({ sessionId: result.checkout_session_id });
                    if (redirect.error) {
                        throw new Error(redirect.error.message || "Unable to redirect to Stripe checkout.");
                    }
                    return;
                }
            }

            if (result.checkout_url) {
                window.location.href = result.checkout_url;
                return;
            }

            setSubscription(result.subscription);
            setMessage("Subscription updated.");
            await refresh();
            await loadSubscription();
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || "Unable to start checkout.";
            setMessage(detail);
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handlePortal = async () => {
        setPortalLoading(true);
        setMessage(null);
        try {
            const result = await subscriptionApi.createBillingPortal(`${window.location.origin}/app/billing`);
            if (result.portal_url) {
                window.location.href = result.portal_url;
                return;
            }
            setMessage("Unable to open Stripe portal right now.");
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || "Unable to open Stripe portal.";
            setMessage(detail);
        } finally {
            setPortalLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("Cancel Pro renewal at period end? You keep full Pro access until your current billing cycle ends.")) return;
        setCancelLoading(true);
        setMessage(null);
        try {
            const sub = await subscriptionApi.cancel(false);
            setSubscription(sub);
            await refresh();
            const finalDate = formatDate(sub?.access_until || sub?.cancel_at);
            setMessage(
                sub?.cancel_at_period_end
                    ? `Cancellation scheduled for ${finalDate || "period end"}.`
                    : "Subscription canceled and switched to Free.",
            );
            await loadSubscription();
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || "Unable to cancel subscription.";
            setMessage(detail);
        } finally {
            setCancelLoading(false);
        }
    };

    const handleResume = async () => {
        setResumeLoading(true);
        setMessage(null);
        try {
            const sub = await subscriptionApi.resume();
            setSubscription(sub);
            await refresh();
            setMessage("Cancellation removed. Your Pro plan will renew normally.");
            await loadSubscription();
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || "Unable to resume subscription.";
            setMessage(detail);
        } finally {
            setResumeLoading(false);
        }
    };

    return (
        <PageContainer>
            <div className="space-y-6 fade-in">
                <div>
                    <h1 className="text-3xl font-bold text-theme tracking-tight">Billing</h1>
                    <p className="text-theme-secondary mt-1">Plan management, payments, and invoice history.</p>
                </div>

                {message && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-center gap-2">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {message}
                    </div>
                )}

                {trialEndedLine && (
                    <div className="rounded-xl border border-slate-500/30 bg-slate-500/10 px-4 py-3 text-sm text-slate-100 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {trialEndedLine}
                    </div>
                )}

                {typeof trialDaysLeft === "number" && trialEndingSoon && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {trialEndingSoonBannerLine(trialDaysLeft)}
                    </div>
                )}

                {typeof trialDaysLeft === "number" && !trialEndingSoon && (
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {trialActiveCalmLine(trialDaysLeft)}
                    </div>
                )}

                {cancellationScheduled && (
                    <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-sky-100 flex flex-wrap items-center justify-between gap-3">
                        <div>{cancellationBannerLine}</div>
                        {canResume && (
                            <button
                                onClick={handleResume}
                                disabled={resumeLoading}
                                className={`px-4 py-2 rounded-lg border text-xs font-semibold transition-all ${resumeLoading
                                    ? "bg-theme-secondary border-theme text-theme-muted cursor-not-allowed opacity-60"
                                    : "bg-sky-400 text-slate-950 border-transparent hover:bg-sky-300"
                                    }`}
                            >
                                {resumeLoading ? "Resuming..." : "Resume Subscription"}
                            </button>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2 space-y-6">
                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-electric-blue shadow-sm space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-bold text-theme flex items-center gap-2">
                                        <FontAwesomeIcon icon={faBolt} className="text-electric-blue" />
                                        {effectivePlan === "pro" ? "Current Plan" : "Free during beta"}
                                    </h3>
                                    <p className="text-sm text-theme mt-2">
                                        {effectivePlan === "pro"
                                            ? `Tier: ${effectivePlan.toUpperCase()} · Status: ${effectiveStatus.toUpperCase()}`
                                            : `Free during beta — keep using ${BRAND_NAME}. Paid Pro comes after beta.`}
                                    </p>
                                    {effectivePlan !== "pro" && (
                                        <p className="text-xs text-theme-secondary mt-1">
                                            Tier: {effectivePlan.toUpperCase()} · Status: {effectiveStatus.toUpperCase()}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <details className="text-sm text-theme-secondary" data-testid="billing-after-beta">
                                <summary className="cursor-pointer text-xs">Coming after beta</summary>
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className={`rounded-xl border p-4 cursor-pointer transition-all ${planCycle === "monthly" ? "border-indigo-500/50 bg-indigo-500/10" : "border-theme bg-theme-tertiary/40"}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-theme">Pro Monthly</span>
                                            <input
                                                type="radio"
                                                checked={planCycle === "monthly"}
                                                onChange={() => setPlanCycle("monthly")}
                                            />
                                        </div>
                                        <div className="text-xs text-theme-secondary mt-1">$9.99 per month</div>
                                    </label>
                                    <label className={`rounded-xl border p-4 cursor-pointer transition-all ${planCycle === "annual" ? "border-indigo-500/50 bg-indigo-500/10" : "border-theme bg-theme-tertiary/40"}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-theme">Pro Annual</span>
                                            <input
                                                type="radio"
                                                checked={planCycle === "annual"}
                                                onChange={() => setPlanCycle("annual")}
                                            />
                                        </div>
                                        <div className="text-xs text-theme-secondary mt-1">
                                            $99 per year <span className="text-emerald-400">({annualEffectiveMonthly}/mo effective)</span>
                                        </div>
                                    </label>
                                </div>
                                <p className="text-xs text-theme-secondary mt-2">
                                    Annual plan saves {annualSavingsPercent}% and works out to {annualEffectiveMonthly}/mo.
                                </p>
                                {effectivePlan !== "pro" && (
                                    <button
                                        type="button"
                                        onClick={handleUpgrade}
                                        disabled={checkoutLoading}
                                        className="mt-3 px-4 py-2 rounded-lg border border-theme text-xs font-semibold text-theme-secondary"
                                    >
                                        {checkoutLoading ? "Redirecting..." : "Upgrade Now"}
                                    </button>
                                )}
                            </details>

                            <div className="flex flex-wrap items-center gap-3">
                                {effectivePlan === "pro" && (
                                <button
                                    onClick={handleUpgrade}
                                    disabled={checkoutLoading}
                                    className={`px-5 py-3 rounded-lg border font-bold transition-all ${checkoutLoading
                                        ? "bg-theme-secondary border-theme text-theme-muted cursor-not-allowed opacity-60"
                                        : "bg-indigo-500 hover:bg-indigo-600 border-transparent text-white shadow-lg shadow-indigo-500/20"
                                        }`}
                                >
                                    {checkoutLoading ? "Redirecting..." : "Change Plan"}
                                </button>
                                )}
                                {effectivePlan === "pro" && !cancellationScheduled && (
                                    <button
                                        onClick={handleCancel}
                                        disabled={cancelLoading}
                                        className={`px-5 py-3 rounded-lg border font-bold transition-all ${cancelLoading
                                            ? "bg-theme-secondary border-theme text-theme-muted cursor-not-allowed opacity-60"
                                            : "bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10"
                                            }`}
                                    >
                                        {cancelLoading ? "Scheduling..." : "Cancel at Period End"}
                                    </button>
                                )}
                                <button
                                    onClick={handlePortal}
                                    disabled={portalLoading}
                                    className={`px-5 py-3 rounded-lg border font-bold transition-all ${portalLoading
                                        ? "bg-theme-secondary border-theme text-theme-muted cursor-not-allowed opacity-60"
                                        : "bg-theme-tertiary border-theme text-theme hover:bg-theme-card-hover"
                                        }`}
                                >
                                    {portalLoading ? "Opening..." : "Open Stripe Portal"}
                                </button>
                            </div>
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-bold text-theme">AI Image → Task Credits</h3>
                                    <p className="text-xs text-theme-secondary mt-1">
                                        1 credit = generate tasks from 1 uploaded image using AI.
                                    </p>
                                </div>
                                <span className="px-3 py-1 rounded-full border border-theme text-xs text-theme-secondary bg-theme-tertiary">
                                    {limitsUnavailable || remainingCredits == null ? "—" : `${remainingCredits} remaining`}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div className="rounded-lg border border-theme bg-theme-tertiary/40 p-3">
                                    <div className="text-theme-muted text-[11px] uppercase tracking-wider">Used / Included</div>
                                    <div className="text-theme font-semibold mt-1">
                                        {limitsUnavailable || usedCredits == null || includedCredits == null
                                            ? "—"
                                            : `${usedCredits} / ${includedCredits}`}
                                    </div>
                                </div>
                                <div className="rounded-lg border border-theme bg-theme-tertiary/40 p-3">
                                    <div className="text-theme-muted text-[11px] uppercase tracking-wider">Remaining</div>
                                    <div className="text-theme font-semibold mt-1">
                                        {limitsUnavailable || remainingCredits == null ? "—" : remainingCredits}
                                    </div>
                                </div>
                                <div className="rounded-lg border border-theme bg-theme-tertiary/40 p-3">
                                    <div className="text-theme-muted text-[11px] uppercase tracking-wider">Reset Date</div>
                                    <div className="text-theme font-semibold mt-1">{creditResetDate || "Next cycle"}</div>
                                </div>
                            </div>
                            {remainingCredits != null && remainingCredits <= 0 && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                                    AI extraction is paused until reset. You can still upload images and add tasks manually.
                                </div>
                            )}
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-cyan-500 shadow-sm space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-bold text-theme">Gmail Mailboxes</h3>
                                    <p className="text-xs text-theme-secondary mt-1">
                                        Connected {connectedMailboxes ?? "—"} / {mailboxLimit == null ? "Unlimited" : mailboxLimit} included mailboxes.
                                    </p>
                                </div>
                                {mailboxLimit != null && connectedMailboxes != null && connectedMailboxes >= mailboxLimit ? (
                                    effectivePlan === "pro" ? (
                                    <button
                                        onClick={handleUpgrade}
                                        disabled={checkoutLoading}
                                        className="px-4 py-2 rounded-lg border border-theme text-xs font-semibold text-theme"
                                    >
                                        {checkoutLoading ? "Redirecting..." : "Change Plan"}
                                    </button>
                                    ) : (
                                    <p className="text-xs text-theme-secondary">More mailboxes come after beta.</p>
                                    )
                                ) : (
                                    <button
                                        onClick={() => navigate("/app/profile")}
                                        className="px-4 py-2 rounded-lg border border-theme text-xs font-semibold text-theme hover:bg-theme-card-hover"
                                    >
                                        Manage Mailboxes
                                    </button>
                                )}
                            </div>
                            <div className="text-xs text-theme-secondary">
                                Additional mailbox add-ons: {mailboxAddonPrice}/month per mailbox (activation support coming soon).
                            </div>
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-slate-500 shadow-sm">
                            <h3 className="text-lg font-bold text-theme flex items-center gap-2 mb-3">
                                <FontAwesomeIcon icon={faReceipt} className="text-slate-300" />
                                Invoices
                            </h3>
                            {billingLoading ? (
                                <div className="text-sm text-theme-secondary">Loading invoices...</div>
                            ) : safeBillingHistory.length === 0 ? (
                                <div className="text-sm text-theme-secondary">No invoices yet.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-theme-secondary text-xs uppercase tracking-wider">
                                                <th className="text-left py-2">Date</th>
                                                <th className="text-left py-2">Amount</th>
                                                <th className="text-left py-2">Status</th>
                                                <th className="text-left py-2">PDF</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {safeBillingHistory.map((invoice) => (
                                                <tr key={invoice.id} className="border-t border-theme text-theme-secondary">
                                                    <td className="py-2">{invoice.date ? new Date(invoice.date).toLocaleDateString() : "—"}</td>
                                                    <td className="py-2">{formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}</td>
                                                    <td className="py-2">{invoice.status || "—"}</td>
                                                    <td className="py-2">
                                                        {invoice.invoice_pdf ? (
                                                            <a
                                                                className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1"
                                                                href={invoice.invoice_pdf}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Download
                                                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                                                            </a>
                                                        ) : "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-emerald-500 shadow-sm">
                            <h3 className="text-base font-bold text-theme flex items-center gap-2">
                                <FontAwesomeIcon icon={faGem} className="text-emerald-400" />
                                Why Pro
                            </h3>
                            <ul className="mt-3 space-y-2 text-sm text-theme-secondary">
                                <li>{`Up to ${proCreditsPreview} AI Image → Task Credits each month`}</li>
                                <li>{`${proMailboxPreview} connected Gmail mailbox${proMailboxPreview === 1 ? "" : "es"} included`}</li>
                                <li>Custom life areas, and {BRAND_NAME} guesses where new items belong — you can fix it in one step.</li>
                                <li>Sync mail into Triage. You start, snooze, or dismiss — {BRAND_NAME} does not run your life alone.</li>
                            </ul>
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-indigo-500 shadow-sm">
                            <h3 className="text-base font-bold text-theme flex items-center gap-2">
                                <FontAwesomeIcon icon={faCreditCard} className="text-indigo-400" />
                                Pricing Snapshot
                            </h3>
                            <div className="mt-3 space-y-2 text-sm text-theme-secondary" data-testid="billing-pricing-snapshot">
                                {effectivePlan === "pro" ? (
                                    <>
                                        <p className="text-theme font-semibold">Pro</p>
                                        <p>Your Pro plan is active.</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-theme font-semibold">Free during beta</p>
                                        <p>Paid Pro comes after beta.</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
};

export default Billing;
